import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Store, Package, Search, ArrowRight, Sparkles, Globe, MapPin, MessageCircle, Instagram, Loader2, AlertCircle } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

interface MarketProduct {
  id: number; name: string; price: string; imageUrl: string | null; category: string | null; isAvailable: boolean;
}
interface MarketStore {
  slug: string; displayName: string; tagline: string | null;
  logoUrl: string | null; whatsapp: string | null; instagram: string | null;
  address: string | null; primaryColor: string; products: MarketProduct[];
}

export default function Marketplace() {
  const { isAuthenticated, user } = useAuth();
  const canManageStore = isAuthenticated && (user?.role === "admin" || user?.role === "superadmin");
  const storeActionHref = canManageStore ? "/ecommerce" : isAuthenticated ? "/" : "/login";
  const storeActionLabel = canManageStore ? "Manage My Store" : isAuthenticated ? "Back to RetailPulse" : "Open My Store";
  const [stores, setStores] = useState<MarketStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    fetch(`${BASE}/api/ecommerce/marketplace`, { signal: controller.signal })
      .then(r => {
        if (!r.ok) throw new Error(`Marketplace request failed (${r.status})`);
        return r.json();
      })
      .then(d => {
        if (!Array.isArray(d)) throw new Error("Invalid marketplace response");
        if (!controller.signal.aborted) setStores(d);
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [retryCount]);

  const filtered = stores.filter(s =>
    !search || s.displayName.toLowerCase().includes(search.toLowerCase()) ||
    s.tagline?.toLowerCase().includes(search.toLowerCase()) ||
    s.products.some(p => p.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-slate-100 bg-white/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2.5 cursor-pointer">
              <img src={LOGO} alt="RetailPulse" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">RetailPulse</span>
              <span className="hidden sm:inline text-slate-400 text-sm">/ Marketplace</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link href={isAuthenticated ? storeActionHref : "/signup"}>
              <Button size="sm" className="gap-1.5">{isAuthenticated ? storeActionLabel : "Sell on RetailPulse"}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm mb-6">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-slate-200">Nigerian Retailers, Online</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4">
            Discover Nigerian Stores
          </h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-8">
            Shop directly from verified local retailers. Fast delivery, authentic products, fair prices.
          </p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search stores or products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-white/40"
            />
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : loadError ? (
          <div className="text-center py-20" role="alert">
            <AlertCircle className="w-14 h-14 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Stores couldn't load</h2>
            <p className="text-slate-500 mb-6">We couldn't reach the marketplace. Please try again.</p>
            <Button onClick={() => setRetryCount(count => count + 1)}>Try Again</Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Store className="w-14 h-14 mx-auto text-slate-200 mb-4" />
            {search ? (
              <>
                <h2 className="text-xl font-semibold text-slate-700 mb-2">No results for "{search}"</h2>
                <p className="text-slate-400">Try a different search term or browse all stores below.</p>
                <Button variant="outline" className="mt-4" onClick={() => setSearch("")}>Clear Search</Button>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-slate-700 mb-2">No stores live yet</h2>
                <p className="text-slate-400 mb-6">Be the first to open your online store on RetailPulse!</p>
                <Link href={isAuthenticated ? storeActionHref : "/signup"}><Button className="gap-2">{isAuthenticated ? storeActionLabel : "Start Selling"} <ArrowRight className="w-4 h-4" /></Button></Link>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-slate-900">{search ? `Results for "${search}"` : "All Stores"}</h2>
              <span className="text-sm text-slate-400">{filtered.length} store{filtered.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(store => {
                const availableProducts = store.products.filter(p => p.isAvailable);
                return (
                  <a key={store.slug} href={`${BASE}/shop/${store.slug}`} className="group block bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:border-slate-200 transition-all">
                    {/* Store header */}
                    <div className="h-24 relative flex items-end p-4" style={{ background: `linear-gradient(135deg, ${store.primaryColor}, ${store.primaryColor}bb)` }}>
                      <div className="flex items-end gap-3">
                        {store.logoUrl ? (
                          <img src={store.logoUrl} alt={store.displayName} className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-white/20 border-2 border-white/40 flex items-center justify-center text-white font-bold text-lg shadow">
                            {store.displayName[0]}
                          </div>
                        )}
                        <div className="pb-0.5">
                          <p className="font-bold text-white">{store.displayName}</p>
                          {store.tagline && <p className="text-white/70 text-xs line-clamp-1">{store.tagline}</p>}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      {/* Meta */}
                      <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                        {store.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{store.address}</span>}
                        {store.whatsapp && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />WhatsApp</span>}
                        {store.instagram && <span className="flex items-center gap-1"><Instagram className="w-3 h-3" />{store.instagram}</span>}
                      </div>

                      {/* Product previews */}
                      {availableProducts.length > 0 ? (
                        <div className="grid grid-cols-3 gap-1.5">
                          {availableProducts.slice(0, 3).map(p => (
                            <div key={p.id} className="aspect-square rounded-lg bg-slate-50 overflow-hidden">
                              {p.imageUrl ? (
                                <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-16 bg-slate-50 rounded-xl flex items-center justify-center">
                          <p className="text-xs text-slate-400">No products listed</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs text-slate-400">{availableProducts.length} product{availableProducts.length !== 1 ? "s" : ""}</span>
                        <span className="text-xs font-medium flex items-center gap-1" style={{ color: store.primaryColor }}>
                          Shop now <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* CTA for sellers */}
      <section className="bg-indigo-50 border-t border-indigo-100 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Globe className="w-10 h-10 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 mb-3">{canManageStore ? "Manage Your Online Store" : "Open Your Online Store"}</h3>
          <p className="text-slate-500 mb-6">Already on RetailPulse POS? Set up your online store in minutes and start selling to customers across Nigeria.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href={storeActionHref}><Button className="gap-2 bg-indigo-600 hover:bg-indigo-700">{storeActionLabel} <ArrowRight className="w-4 h-4" /></Button></Link>
            {!isAuthenticated && <Link href="/signup"><Button variant="outline" className="gap-2">Start Free Trial</Button></Link>}
          </div>
        </div>
      </section>

      <footer className="border-t py-6 px-4 text-center">
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <img src={LOGO} alt="RetailPulse" className="w-5 h-5 rounded" />
          © {new Date().getFullYear()} RetailPulse · POS & E-Commerce for Nigerian Retailers
        </div>
      </footer>
    </div>
  );
}
